import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { ReduxControls, Button, Functions as FN } from "rhombus-react";

import { loadPACTypes } from "reducers/Social";
import { Field } from "redux-form";

import { FACEBOOK_PAC_BY_ADTYPE } from "./constants";

import "./PlacementSelect.css";

const DEFAULT_SEARCH_TIP = "Select assets for aspect ratios or reels placements. Placement assets override the corresponding aspect ratio assets if provided.";

function buildPlacementLabel(placement = {}) {
	let label = `${placement.name}`;

	if (placement.description && placement.externalId !== "ft_placement_reels") {
		label = `${label} (${placement.description})`;
	}

	return label;
}

function buildPlacementOptions(placementOptions = [], adType = "") {
	const placementOptionsByAdType = placementOptions.filter(v => FACEBOOK_PAC_BY_ADTYPE[adType]?.includes(v.name));
	const filteredAspectRatioOptions = placementOptionsByAdType.filter(v => v?.externalId?.includes("ft_aspect"));
	const filteredPlacementsOptions = placementOptionsByAdType.filter(v => v?.externalId?.includes("ft_placement"));
	const groupedOptions = [
		{
			label: "Aspect Ratio",
			options: filteredAspectRatioOptions,
		},
		{
			label: <p className="normal-group-heading"><b>Placement</b> (Overrides Aspect Ratio Assets)</p>,
			options: filteredPlacementsOptions,
		}
	];

	return groupedOptions;
}

export default function PlacementSelect(props) {
	/**
	 * PlacementSelect hooks into a placeholder field `_select-aspect-ratios` to allow for field validation on
	 * how many placements are selected. placements can be any combination of active (T/F) and selected (T/F).
	 *
	 * We show a warning when none are selected and none are active.
	 */
	const {
		onAddPlacements,
		onChange,
		value,
		filterOption,
		showPristineErrors,
		isDisabled,
		adType,
		selectProps,
	} = props;

	const { searchTooltip = DEFAULT_SEARCH_TIP, buttonTooltip } = selectProps;

	const dispatch = useDispatch();
	const { isLoaded, placementTypes } = useSelector(state => ({
		isLoaded: state.social?.pacTypes?.isLoaded,
		placementTypes: state.social?.pacTypes?.aspectRatios,
	}));

	const [placementOptions, setPlacementOptions] = useState([]);
	const filteredPlacementsOptions = buildPlacementOptions(placementOptions, adType);

	useEffect(() => {
		dispatch(loadPACTypes());
	}, []);

	useEffect(() => {
		const options = placementTypes?.map(placement => ({
			...placement,
			label: buildPlacementLabel(placement),
			value: placement.name,
		})) || [];

		setPlacementOptions(options);
	}, [placementTypes]);

	return (
		<>
			<Field
				component={ReduxControls.Select}
				label="Select Aspect Ratios/Placement"
				placeholder="Search and Select Aspect Ratios..."
				className="pac-select"
				icon="search"
				name="_select-aspect-ratios"
				options={filteredPlacementsOptions}
				isLoading={!isLoaded}
				isDisabled={isDisabled || !isLoaded}
				onChange={onChange}
				filterOption={filterOption} // When filterOption is present, search functionality is overriden by it
				tip={{
					direction: "bottom",
					size: "large",
					description: searchTooltip,
				}}
				formatOptionLabel={(option, { context }) => {
					/* context can be either `menu` or `value`
						menu - dropdown
						value - value displayed
					*/
					return context === 'menu' ? option.label : option.name;
				}}
				onMenuScrollToBottom={FN.noop}
				showPristineErrors={showPristineErrors}
				showIcon
				isMulti
			/>
			<div className="pac-add-button" data-tip={buttonTooltip}>
				<Button
					/*
						If you remove the last tag, the value changes to `null`, instead of [],
						so value.length isn't always a safe access
					*/
					disabled={!value || (value.length === 0)}
					onClick={onAddPlacements}
				>
					Add
				</Button>
			</div>
		</>
	);
}

PlacementSelect.defaultProps = {
	selectProps: {},
};

PlacementSelect.propTypes = {
	onAddPlacements: PropTypes.func,
	onChange: PropTypes.func,
	value: PropTypes.array,
	filterOption: PropTypes.func,
	selectProps: PropTypes.object,
};
